import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { whatsappService } from '../services/whatsappService';
import toast from 'react-hot-toast';

/**
 * Hook customizado para gerenciar integração WhatsApp Business
 *
 * Features:
 * - Busca status de conexão automaticamente
 * - Auto-refetch a cada 30 segundos
 * - Mutation para desconectar conta
 * - Toast notifications para feedback
 */
export const useWhatsAppIntegration = (googleId: string | undefined) => {
  const queryClient = useQueryClient();

  // Query para buscar status da integração
  const {
    data: statusResponse,
    isLoading: isLoadingStatus,
    refetch: refetchStatus,
    error: statusError,
    isFetching,
  } = useQuery({
    queryKey: ['whatsapp-integration-status', googleId],
    queryFn: () => whatsappService.getIntegrationStatus(googleId!),
    enabled: !!googleId,
    staleTime: 30000, // 30 segundos - considera dados atualizados
    refetchInterval: 60000, // Auto-refetch a cada 60 segundos
    retry: 2, // Tenta 2 vezes em caso de erro
  });

  const status = statusResponse?.data;

  // Mutation para desconectar conta WhatsApp
  const disconnectMutation = useMutation({
    mutationFn: () => whatsappService.disconnectOAuth(googleId!),
    onSuccess: (response) => {
      toast.success(response.message || 'WhatsApp desconectado com sucesso!');
      // Invalida cache e força refetch
      queryClient.invalidateQueries({ queryKey: ['whatsapp-integration-status', googleId] });
      refetchStatus();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Erro ao desconectar WhatsApp';
      toast.error(errorMessage);
      console.error('Erro ao desconectar WhatsApp:', error);
    },
  });

  return {
    // Dados do status
    status,
    hasIntegration: status?.hasIntegration || false,
    isActive: status?.isActive || false,
    phoneNumber: status?.phoneNumber,
    phoneNumberId: status?.phoneNumberId,
    whatsappAccountId: status?.whatsappAccountId,
    verifiedName: status?.verifiedName,
    qualityRating: status?.qualityRating,
    lastError: status?.lastError,
    integrationStatus: status?.status,

    // Loading states
    isLoadingStatus,
    isFetching, // True quando está fazendo refetch em background
    statusError,

    // Actions
    refetchStatus,
    disconnectAccount: disconnectMutation.mutate,
    isDisconnecting: disconnectMutation.isPending,
  };
};
